import React, { useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Typography } from "@mui/material";
import Badge from "@components/Badge/Badge";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import StepperControls from "@components/StepperControls/StepperControls";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE } from "@components/StepperTabs/StepperTabs.types";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  PENDING_APPROVAL_STATUS,
  SUBMIT_FROM_STATUSES,
  TOTAL_QUANTITY_EDITABLE_STATUSES,
  WAITING_FINAL_DRAFT_SURVEY_STATUS,
} from "@features/drybulk-cargo-operations/constants";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutlet, useParams } from "react-router-dom";
import FinalDraftSurveyEntry from "../closeOut/FinalDraftSurveyEntry";
import OperationDelaysTab from "../delays/OperationDelaysTab";
import ExecutionProvider from "../execution/ExecutionProvider";
import TransportersAccordion from "../execution/TransportersAccordion";
import FinalReportTab from "../finalReport/FinalReportTab";
import PollingGateProvider from "../hooks/PollingGateProvider";
import { useCachedCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useDetailExtensions } from "../hooks/useDetailExtensions";
import { useIsCargoOpsShipper } from "../hooks/useIsCargoOpsShipper";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import {
  hasVisitChanges,
  snapshotOf,
  useUndoDraftVisit,
  type VisitSnapshot,
} from "../hooks/useUndoDraftVisit";
import MessagesTab from "../messages/MessagesTab";
import OperationLogTab from "../operationLog/OperationLogTab";
import { sequenceDetailsPath } from "../utils/cargoOpsPaths";
import { operationStatusBadgeType } from "../utils/statusBadgeType";
import { statusLabel } from "../utils/statusLabel";
import type { DetailExtensions } from "./detailExtensions";
import DraftActions, { type DraftActionsHandle } from "./DraftActions";
import OperationDocumentsTab, { type PendingDocumentsForm } from "./OperationDocumentsTab";
import OperationNominations from "./OperationNominations";
import OperationsInfoCard from "./OperationsInfoCard";
import OperationsReviewActions from "./OperationsReviewActions";
import RejectedActions from "./RejectedActions";
import RejectionReasonBanner from "./RejectionReasonBanner";
import SequenceSection from "./SequenceSection";
import ShipperReadinessAction from "./ShipperReadinessAction";
import TotalQuantityEditor, { type TotalQuantityEditorHandle } from "./TotalQuantityEditor";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail`;

const DrybulkCargoOperationDetail: React.FC<{ extensions?: DetailExtensions }> = ({
  extensions: inherited,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const { operationId } = useParams<{ operationId: string }>();
  const totalRef = useRef<TotalQuantityEditorHandle>(null);
  const actionsRef = useRef<DraftActionsHandle>(null);
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const snapshot = useRef<VisitSnapshot | null>(null);
  const visitStartedAt = useRef({ operationId, at: Date.now() });
  if (visitStartedAt.current.operationId !== operationId) {
    visitStartedAt.current = { operationId, at: Date.now() };
  }
  const detail = useCachedCargoOperationDetail(operationId);
  const data = detail.data;
  const showDraftActions = data?.status === "DRAFT" && isShippingAgent;
  const showRejectedActions = data?.status === "REJECTED" && isShippingAgent;
  const [totalEditing, setTotalEditing] = useState(false);
  useEffect(() => {
    snapshot.current = null;
  }, [operationId]);
  useEffect(() => {
    if (data && !snapshot.current && detail.dataUpdatedAt >= visitStartedAt.current.at) {
      snapshot.current = snapshotOf(data);
    }
  }, [data, detail.dataUpdatedAt]);
  const undoVisit = useUndoDraftVisit(operationId ?? "", snapshot);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const isOperationsUser = useAuthStore((state) => state.isOperationsUser);
  const showReviewActions = isOperationsUser && data?.status === PENDING_APPROVAL_STATUS;
  const extensions = useDetailExtensions(operationId, data, isOperationsUser, inherited);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const isShipper = useIsCargoOpsShipper();
  const [readinessDialogOpen, setReadinessDialogOpen] = useState(false);
  const documentsForm = useForm<PendingDocumentsForm>({ defaultValues: { rows: [] } });

  return (
    <PollingGateProvider>
      <ExecutionProvider>
        <Box hidden={outlet !== null} data-testid="drybulk-cargo-operation-body">
          <Box
            data-testid="drybulk-cargo-operation-detail"
            sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 2 }}
          >
            <LoadingOverlay loading={detail.isLoading} testId="drybulk-operation-detail-loading" />
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              <IconButton
                size="medium"
                sx={{ color: "text.primary", mr: 1 }}
                aria-label={t("common.back")}
                data-testid="drybulk-cargo-operation-back-arrow"
                onClick={() =>
                  showDraftActions
                    ? actionsRef.current?.cancel()
                    : navigate("..", { relative: "path" })
                }
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  data-testid="drybulk-cargo-operation-detail-title"
                >
                  {data?.operationNumber !== null &&
                  data?.operationNumber !== undefined &&
                  data.operationType
                    ? t(`${L}.title-${data.operationType === "LOAD" ? "loading" : "unloading"}`, {
                        number: data.operationNumber,
                      })
                    : t(`${L}.title`)}
                </Typography>
                {data && (
                  <Badge
                    type={operationStatusBadgeType(data.status)}
                    label={statusLabel(t, data.status)}
                    testId="drybulk-cargo-operation-detail-status-badge"
                    labelTestId="drybulk-cargo-operation-detail-status"
                  />
                )}
                {extensions.headerChips && (
                  <Box
                    data-testid="drybulk-cargo-operation-header-chips"
                    sx={{ display: "flex", gap: 1 }}
                  >
                    {extensions.headerChips}
                  </Box>
                )}
              </Box>
              <Box sx={{ ml: "auto" }} data-testid="drybulk-cargo-operation-header-actions">
                {showReviewActions && operationId && (
                  <OperationsReviewActions
                    operationId={operationId}
                    onDialogOpenChange={setReviewDialogOpen}
                  />
                )}
                {isShipper && operationId && (
                  <ShipperReadinessAction
                    operationId={operationId}
                    onDialogOpenChange={setReadinessDialogOpen}
                  />
                )}
              </Box>
            </Box>
            {data?.status === "REJECTED" && data.latestRejection && (
              <RejectionReasonBanner rejection={data.latestRejection} />
            )}
            {data && (
              <Box
                data-testid="drybulk-cargo-operation-detail-header"
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                <OperationsInfoCard
                  detail={data}
                  totalQuantity={
                    <TotalQuantityEditor
                      ref={totalRef}
                      operationId={data.id}
                      totalQuantity={data.totalQuantity}
                      remained={data.totalQuantityRemained}
                      editable={
                        !isOperationsUser &&
                        TOTAL_QUANTITY_EDITABLE_STATUSES.includes(data.status ?? "")
                      }
                      showRemained={isShippingAgent}
                      inCard
                      onEditingChange={setTotalEditing}
                    />
                  }
                />
                <OperationNominations operationId={data.id} />
                {isShippingAgent && data.status === WAITING_FINAL_DRAFT_SURVEY_STATUS && (
                  <FinalDraftSurveyEntry
                    operationId={data.id}
                    shipperCodes={(data.shippers ?? []).flatMap((row) =>
                      row.shipperCompanyCode ? [row.shipperCompanyCode] : [],
                    )}
                  />
                )}
              </Box>
            )}
            {operationId ? (
              <StepperTabs
                testId="drybulk-cargo-operation-tabs"
                stepperType={STEPPER_TYPE.HORIZONTAL}
                isTypeChangeable={false}
              >
                <StepperTabPanel label={t(`${L}.tabs.operation-details`)}>
                  <SequenceSection
                    operationId={operationId}
                    extensions={extensions}
                    onOpenSequence={(sequence) => navigate(sequenceDetailsPath(sequence.id))}
                    pausePolling={
                      outlet !== null ||
                      totalEditing ||
                      (showReviewActions && reviewDialogOpen) ||
                      (isShipper && readinessDialogOpen) ||
                      (submitDialogOpen && SUBMIT_FROM_STATUSES.includes(data?.status ?? ""))
                    }
                  />
                  {data?.transporters && <TransportersAccordion transporters={data.transporters} />}
                </StepperTabPanel>
                {isOperationsUser && (
                  <StepperTabPanel label={t(`${L}.tabs.delays`)}>
                    <OperationDelaysTab operationId={operationId} status={data?.status} />
                  </StepperTabPanel>
                )}
                {data?.attachments !== undefined && (
                  <StepperTabPanel label={t(`${L}.tabs.documents`)}>
                    <OperationDocumentsTab operationId={operationId} form={documentsForm} />
                  </StepperTabPanel>
                )}
                <StepperTabPanel label={t(`${L}.tabs.messages`)}>
                  <MessagesTab operationId={operationId} />
                </StepperTabPanel>
                <StepperTabPanel label={t(`${L}.tabs.operation-log`)}>
                  <OperationLogTab operationId={operationId} status={data?.status} />
                </StepperTabPanel>
                {isOperationsUser && (
                  <StepperTabPanel label={t(`${L}.tabs.final-report`)}>
                    <FinalReportTab operationId={operationId} status={data?.status} />
                  </StepperTabPanel>
                )}
              </StepperTabs>
            ) : (
              <div className="relative min-h-[240px]">
                <LoadingOverlay loading />
              </div>
            )}
            {showDraftActions && (
              <DraftActions
                ref={actionsRef}
                operationId={data.id}
                status={data.status}
                onLeave={() => navigate("..", { relative: "path" })}
                undoVisit={async () => {
                  totalRef.current?.discardEdit();
                  return undoVisit();
                }}
                hasChanges={totalEditing || hasVisitChanges(snapshot.current, data)}
                onDialogOpenChange={setSubmitDialogOpen}
                beforeSaveDraft={() => totalRef.current?.commitPending() ?? Promise.resolve(true)}
              />
            )}
            {showRejectedActions && (
              <RejectedActions
                operationId={data.id}
                status={data.status}
                onBack={() => navigate("..", { relative: "path" })}
                onDialogOpenChange={setSubmitDialogOpen}
              />
            )}
            {data && !showDraftActions && !showRejectedActions && !isOperationsUser && (
              <Box data-testid="drybulk-cargo-operation-back">
                <StepperControls
                  prev={() => navigate("..", { relative: "path" })}
                  backLabelTranslationKey="common.back"
                />
              </Box>
            )}
          </Box>
        </Box>
        {outlet}
      </ExecutionProvider>
    </PollingGateProvider>
  );
};

export default DrybulkCargoOperationDetail;
